import { Effect, Option } from 'effect';
import { last } from 'effect/Array';

import { MissingIdSegmentError } from '@vs-shop/engine/data/error/router';

export const extractId = (urlWithId: string) => {
  const segments = urlWithId.split('/');
  return last(segments);
};

export const extractIdEffect = (urlWithId: string) =>
  Effect.suspend(() => {
    const id = extractId(urlWithId);

    return Option.match(id, {
      onSome: (id) => Effect.succeed(id),
      onNone: () =>
        Effect.fail(
          new MissingIdSegmentError({
            segments: urlWithId.split('/'),
            message: 'Invalid URL: missing id segment',
          })
        ),
    });
  });

/**
 * Extracts order token from payment @id URL
 * Example: "/api/v2/shop/orders/JwoLCdxVMX_XDA5-xCRJa3KOx4LoWM_RNKKRoW8mlzDlw5YOrAJJleZZ2wzO2eQZ/payments/10"
 * Returns: "JwoLCdxVMX_XDA5-xCRJa3KOx4LoWM_RNKKRoW8mlzDlw5YOrAJJleZZ2wzO2eQZ"
 */
export const extractOrderTokenFromPaymentId = (paymentId: string): Option.Option<string> => {
  const pathParts = paymentId.split('/');
  const ordersIndex = pathParts.findIndex((part) => part === 'orders');

  if (ordersIndex === -1 || ordersIndex + 1 >= pathParts.length) {
    return Option.none();
  }

  const orderToken = pathParts[ordersIndex + 1];
  return Option.fromNullishOr(orderToken);
};

/**
 * Performs full browser navigation (not Next.js client-side routing)
 * Use this when you need to completely reload the page and skip client-side routing
 *
 * @param url - The URL to navigate to (relative or absolute)
 */
export const navigateFullPage = (url: string): void => {
  window.location.href = url;
};

/**
 * Navigate back in browser history using native browser navigation
 * Use this instead of router.back() for full page reloads
 */
export const navigateBack = (): void => {
  window.history.back();
};

export const reloadPage = (): void => {
  window.location.reload();
};
